import type {
  OpenEndedBlockResponse,
  TaskContent,
} from '@assembly-web/services';
import { MemberState, TaskState } from '@assembly-web/services';
import { CheckCircleIcon as CheckOutlinedCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckSolidCircleIcon } from '@heroicons/react/24/solid';
import type { ReactNode } from 'react';
import reactStringReplace from 'react-string-replace';

import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import { classNames } from '../../../DesignSystem/Utils/classNames';
import { PostCardActions } from './PostCard';

const MENTION_REGEX = new RegExp(/@__(.*?)__@/);
const URL_REGEX = new RegExp(
  /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi
);

const convertTokenizedObjectToString = (content: TaskContent[]) =>
  content.reduce((acc, obj) => {
    if (obj.type === 'text') {
      return acc + obj.value || '';
    }
    return `${acc}\n`;
  }, '');

export const getFormattedMessage = (
  openEndedBlockResponse: OpenEndedBlockResponse,
  onClick: (type: PostCardActions, id: string) => void,
  labels: Record<string, string>
) => {
  const {
    value,
    mentions = [],
    tags = [],
    tasks = [],
  } = openEndedBlockResponse;

  let messageText: ReactNode[] = [value];

  messageText = reactStringReplace(messageText, '\n', (_, index) => (
    <br key={index} />
  ));

  messageText = reactStringReplace(messageText, URL_REGEX, (match) => {
    return (
      <a
        className="mt-1 h-fit w-fit font-normal text-primary-6"
        href={match}
        target="_blank"
        rel="noopener noreferrer"
      >
        {match}
      </a>
    );
  });
  if (mentions.length || tags.length || tasks.length) {
    messageText = reactStringReplace(messageText, MENTION_REGEX, (match) => {
      if (match.startsWith('tag:')) {
        const matchingTag = tags.find(
          ({ tag }) => match.split('tag:').pop() === tag
        );
        if (matchingTag) {
          return (
            <button
              onClick={() =>
                onClick(PostCardActions.PersonClicked, matchingTag.tag)
              }
              className="h-fit w-fit text-primary-6"
            >
              {matchingTag.displayText}
            </button>
          );
        }
      } else if (match.startsWith('task:')) {
        const matchingTask = tasks.find(
          ({ id }) => match.split('task:').pop() === id
        );
        if (matchingTask) {
          const title = convertTokenizedObjectToString(matchingTask.title);

          return (
            <button
              onClick={() =>
                onClick(PostCardActions.TaskClicked, matchingTask.id)
              }
              className={classNames(
                'mt-1 inline-flex h-fit w-fit items-center rounded bg-gray-3 px-1 text-left align-middle text-sm font-normal',
                {
                  'line-through': matchingTask.state === TaskState.Completed,
                  'hover:bg-gray-4': matchingTask.state !== TaskState.Deleted,
                  'text-gray-9':
                    matchingTask.state === TaskState.Completed ||
                    matchingTask.state === TaskState.Active,
                  'text-gray-7':
                    matchingTask.state === TaskState.Archived ||
                    matchingTask.state === TaskState.Deleted,
                }
              )}
              disabled={matchingTask.state === TaskState.Deleted}
            >
              {matchingTask.state === TaskState.Completed && (
                <CheckSolidCircleIcon className="mr-1 h-4 fill-success-7" />
              )}
              {matchingTask.state === TaskState.Active && (
                <CheckOutlinedCircleIcon className="mr-1 h-4 text-gray-7" />
              )}
              {matchingTask.state === TaskState.Deleted
                ? labels.deletedTask
                : title}
            </button>
          );
        }
      }
      return mentions
        .filter((member) => member.memberId === match)
        .map((member) => {
          if (member.memberState === MemberState.Deactivated) {
            return (
              <TextStyle
                as="span"
                variant="sm-regular"
                className="text-gray-6"
                key={member.memberId}
              >
                {`${member.firstName} ${member.lastName} (deactivated)`}
              </TextStyle>
            );
          } else {
            return (
              <button
                onClick={() =>
                  onClick(PostCardActions.PersonClicked, member.memberId)
                }
                className="h-fit w-fit rounded bg-primary-1 px-1 font-normal text-primary-6 hover:bg-primary-2"
                key={member.memberId}
              >
                {`${member.firstName} ${member.lastName}`}
              </button>
            );
          }
        });
    });
  }
  return messageText;
};
